import { ContactType } from "features/customer/enums/contactType";
import { ContractType } from "features/contracts/enums/contractType";
import { InvoiceType } from "features/invoices/enums/invoiceType";
import { EnabledRequirementType } from "features/requirements/enums/enabledRequirementType";
import { loadPortalConfig } from "./modules";
import { PaymentType } from "features/payments/enums/paymentType";
import { getQueryParams } from "./queryParams";
import { LocationDescriptor } from "history";
import { CommodityDetail } from "features/contracts/enums/commodityDetail";
import { containsFuelingCommodity } from "features/contracts/helpers/commodityDetailHelper";
import { getMeasuredDataType } from "features/shared/components/measuredData/measuredDataHelper";
import { isEnabledCustomForm } from "features/requirements/helpers/requirementTypeHelper";

const portalConfig = loadPortalConfig();

export const getContractDetailRootRoute = (
  contractId: string,
  routeSuffix?: string,
  isFuelingContract?: boolean
) => {
  let suffix = routeSuffix ? `/${routeSuffix}` : "";
  let contractsRoute = isFuelingContract ? "fueling-contracts" : "contracts";

  if (portalConfig.useContractualAccounts) {
    return `/contractual-accounts/${contractsRoute}/${contractId}${suffix}`;
  } else {
    return `/${contractsRoute}/${contractId}${suffix}`;
  }
};

export const getContractDetailRoute = (contractId: string, isFuelingContract?: boolean) => {
  return getContractDetailRootRoute(contractId, "detail", isFuelingContract);
};

export const getContractsRoute = () => {
  if (portalConfig.useContractualAccounts) {
    return `/contractual-accounts`;
  } else {
    return `/contracts`;
  }
};

export const getSelfReadingCreateRoute = (contractId: string) => {
  if (isEnabledCustomForm("ZADANISAMOODECTU")) {
    if (portalConfig.useFullRequirements) {
      return `/requirements/create/${EnabledRequirementType.ZADANISAMOODECTU}`;
    } else {
      return getContractDetailRootRoute(contractId, "self-readings/create");
    }
  }

  return "";
};

export const getReservedCapacityChangeRoute = (
  contractId: string,
  contractType: ContractType,
  capacityType?: "monthly" | "yearly"
) => {
  if (portalConfig.useFullRequirements) {
    let type: string = "";
    switch (contractType) {
      case "ElectricityHighVoltage":
        type =
          EnabledRequirementType.ZMENADISTRIBUCNISAZBY_JISTICE_REZERVOVANEHOPRIKONU;
        break;
      case "GasLargeConsumption":
        type = EnabledRequirementType.ZMENAKAPACITYPLYN;
        break;
    }

    return `/requirements/create/${type}`;
  } else if (
    contractType === "GasLargeConsumption" &&
    isEnabledCustomForm("ZMENAKAPACITYPLYN")
  ) {
    return getContractDetailRootRoute(
      contractId,
      "reserved-capacity/create/gas"
    );
  } else if (
    contractType === "ElectricityHighVoltage" &&
    (isEnabledCustomForm("ZMENAKAPACITYELEKTRINAROCNI") && isEnabledCustomForm("ZMENAKAPACITYELEKTRINAMESICNI"))
  ) {
    return getContractDetailRootRoute(
      contractId,
      `reserved-capacity/create/electricity/${capacityType}`
    );
  }

  return "";
};

export const getInvoiceDetailRoute = (
  type: InvoiceType,
  id: string,
  proformaInvoiceId?: string
): LocationDescriptor => {
  let route: string;
  let seachParams: string | undefined = undefined;

  switch (type) {
    case InvoiceType.Invoice:
      route = `/invoices/${id}/detail`;
      break;
    case InvoiceType.Proformainvoice:
      route = `/proforma-invoices/${id}/detail`;
      if (proformaInvoiceId) {
        seachParams = getQueryParams("proformaInvoiceId", proformaInvoiceId).toString();
      }

      break;
    default:
      route = `/invoices/${id}`;
      break;
  }

  return {
    pathname: route,
    search: seachParams,
  };
};

export const getContactChangeRoute = (contactType: ContactType): string => {
  if (portalConfig.useFullRequirements) {
    switch (contactType) {
      case "Email":
        return `/requirements/create/${EnabledRequirementType.ZMENA_EMAILU}`;
      case "Mobile":
        return `/requirements/create/${EnabledRequirementType.ZMENA_MOBIL_TELEFONU}`;
      default:
        break;
    }
  } else if (contactType === "Email" && isEnabledCustomForm("ZMENA_EMAILU")) {
    return "/customer/change-email";
  } else if (contactType === "Mobile" && isEnabledCustomForm("ZMENA_MOBIL_TELEFONU")) {
    return "/customer/change-mobile";
  } else if (contactType === "Phone" && isEnabledCustomForm("ZMENATELEFONU")) {
    return "/customer/change-phone";
  }

  return "";
};

export const getPaymentDetailRoute = (type: PaymentType, id: string) => {
  switch (type) {
    case PaymentType.FinancialSettlement:
      return `/payments/${id}/financial-settlement`;
    default:
      return `/payments/${id}`;
  }
};

export const getContractualAccountDetailRoute = (
  contractulAccountId: string,
  routeSuffix?: string,
  isFuelingContract?: boolean
): LocationDescriptor => {
  let suffix = routeSuffix ? `/${routeSuffix}` : "/detail";
  let route = `/contractual-accounts/${contractulAccountId}${suffix}`;
  let seachParams: string | undefined = undefined;

  if (isFuelingContract) {
    seachParams = getQueryParams("isFueling", String(true)).toString();
  }


  return {
    pathname: route,
    search: seachParams
  }
};

export const getRouteContainsFuelingParam = (
  route: string,
  commodityDetails: CommodityDetail[],
  fuelingParamName: string = "isFueling"
): LocationDescriptor => {
  let seachParams: string | undefined = undefined;

  if (containsFuelingCommodity(commodityDetails)) {
    seachParams = getQueryParams(fuelingParamName, String(true)).toString();
  }

  return {
    pathname: route,
    search: seachParams,
  };
};

export const getMeasuredDataHistoryRoute = (
  commodityDetail: CommodityDetail,
  isProduction: boolean = false
) => {
  const type  = getMeasuredDataType(commodityDetail, isProduction);
  return `${type}-history`;  
};

export const getContractProcessDetailRoute = (
  contractProcessId: string | number
) => `/contractProcess/${encodeURIComponent(String(contractProcessId))}`;

export const getDepositDetailRoute = (
  depositId: string | number
) => `/deposits/${encodeURIComponent(String(depositId))}`;